// Search across everything the member owns, plus every free item (design 5.3). It runs over the
// rows they can already read, so it respects access by construction (data-model.md, section 6):
// titles of weeks and modules, the recordings and chapters released to them, Start here's steps,
// resources, the masterclass, the free resources and the free webinars. A title not yet released
// shows with its lock line. The words under each result are Main.dc.html's: "The Funding Engine,
// Week 2" for a week, the product alone for an on-demand module, "Free resources, 5 days" for the
// challenge.
import * as f from '../lib/format.js'
import { linkStamp } from '../lib/timestamp.js'
import { replayPath } from '../free/webinars.js'

const MIN = 60e3

export function buildSearchIndex(d, owned, nowMs) {
  const out = []
  const seen = new Set()
  const add = (label, where, href) => {
    const key = label + '|' + where
    if (!label || seen.has(key)) return
    seen.add(key)
    out.push({ label, where, href })
  }
  const openProducts = [...owned.values()].filter(o => o.open).map(o => o.product)
  const recs = d.recordings
  const content = new Map(d.content.map(c => [c.recording_id, c]))
  // a recording's own page: its module's when it's the module's only recording, else its own key
  // (PCFL's Module 3 has Part A, B and C: /cashflow/m3b)
  const pageOf = (base, r, mod) => {
    const parts = mod ? recs.filter(x => x.module_id === mod.id).length : 0
    if (mod && (parts <= 1 || !r.key)) return `${base}/${mod.key}`
    return `${base}/${r.key || ''}`.replace(/\/$/, '')
  }

  for (const p of openProducts) {
    const base = p.path || '/'
    if (p.type === 'masterclass') {
      const r = recs.filter(x => x.product_id === p.id).sort((a, b) => a.sort_order - b.sort_order)[0]
      const dur = r && content.get(r.id)?.duration_seconds
      add(p.name, dur ? `Video, ${f.duration(dur)}` : 'Video', base)
    }

    // weeks and modules, with the lock line while their recording waits for its session
    const mods = d.modules.filter(m => m.product_id === p.id && m.is_published).sort((a, b) => a.sort_order - b.sort_order)
    for (const m of mods) {
      if (f.isPlaceholder(m.title)) continue
      const label = m.label === m.title ? m.title : `${m.label}: ${m.title}`
      const own = recs.filter(r => r.module_id === m.id)
      // open once 1 of its recordings has opened to them: switched on and, for a course that releases
      // by intake, past their own session (its content comes back only then)
      const waiting = own.length > 0 && !own.some(r => r.is_published && Date.parse(r.published_at) <= nowMs && content.has(r.id))
      // an on-demand module's label is already in its title, so the board gives only the product
      const where = p.type === 'on_demand' ? p.name : `${p.name}, ${m.label}`
      add(label, waiting ? `${p.name}, opens after the live session` : where, `${base}/${m.key}`)
    }

    // recordings released to them whose title says more than their module's (PCFL's Module 3 has
    // "Stack it", "Fund it" and "Buy it"); the masterclass is above
    if (p.type !== 'masterclass') {
      for (const r of recs.filter(x => x.product_id === p.id && x.is_published && x.published_at && Date.parse(x.published_at) <= nowMs && content.has(x.id))) {
        const mod = r.module_id && d.modules.find(m => m.id === r.module_id)
        if (f.isPlaceholder(r.title) || (mod && (r.title === mod.title || r.title === mod.label))) continue
        add(r.title, mod ? `${p.name}, ${mod.label}` : p.name, pageOf(base, r, mod))
      }
    }

    // Start here's steps, on the product's home
    for (const st of (d.steps || []).filter(x => x.product_id === p.id && x.is_published && x.section === 'start_here')
      .sort((a, b) => a.sort_order - b.sort_order)) {
      if (f.isPlaceholder(st.title)) continue
      add(st.title, `${p.name}, Start here`, base)
    }

    // chapters of recordings released to them (recording_content comes back only once released)
    for (const r of recs.filter(x => x.product_id === p.id)) {
      const c = content.get(r.id)
      if (!c) continue
      const mod = r.module_id && d.modules.find(m => m.id === r.module_id)
      for (const ch of c.chapters || []) {
        if (f.isPlaceholder(ch.title)) continue
        const where = [p.name, mod ? mod.label : null, `at ${f.stamp(ch.start_seconds)}`].filter(Boolean).join(', ')
        // the minute as the hub writes it everywhere else (08m51s, 1h01m41s)
        add(ch.title, where, `${pageOf(base, r, mod)}?t=${linkStamp(ch.start_seconds)}`)
      }
    }

    // resources: in a week's pack, or on a shelf, the shelf's tab named as the admin names it, its own
    // capitals kept (Adam, 12 Oct: "Profit On Your Terms, Before Week 1")
    for (const pl of d.placements.filter(x => x.product_id === p.id && !x.removed_at)) {
      const item = d.items.find(i => i.id === pl.item_id)
      if (!item) continue
      const mod = pl.module_id && d.modules.find(m => m.id === pl.module_id)
      const section = d.sections.find(s => s.id === pl.section_id)
      const where = mod ? `${p.name}, ${mod.label} resources` : `${p.name}${section ? ', ' + section.name : ''}`
      add(pl.title_override || item.title, where, `${base}/resources`)
    }
  }

  // every free item
  for (const p of d.products.filter(x => x.access_kind === 'free' && x.state === 'live' && (x.type === 'linked' || x.type === 'resource_pack'))
    .sort((a, b) => a.sort_order - b.sort_order)) {
    add(p.name, freeWhere(p.settings), '/free-resources')
  }
  for (const w of d.webinars.filter(x => x.shown)) {
    const start = Date.parse(w.starts_at)
    const ended = start + w.duration_minutes * MIN <= nowMs
    if (ended && !w.replay_up) continue
    // a replay opens its own page, where it plays (Adam, 23 Oct)
    add(w.title, ended ? 'Free webinars, replay' : `Free webinars, ${f.shortDate(start)} at ${f.time(start)}`, ended ? replayPath(w.id) : '/free-webinars')
  }
  return out
}

// "Free resources, scorecard", "Free resources, guide", and for something you join, how long it
// runs: "Free resources, 5 days" (from "Challenge, 5 days"), as Main.dc.html has them
export function freeWhere(settings) {
  const card = settings?.card || {}
  if (card.search_line) return `Free resources, ${card.search_line}`
  const [kind, length] = (card.library_kind_line || '').split(', ')
  if (settings?.join && length) return `Free resources, ${length}`
  const word = card.kind_label || kind
  return word ? `Free resources, ${word.toLowerCase()}` : 'Free resources'
}

export function searchIn(index, query, limit) {
  const q = query.trim().toLowerCase()
  if (!q) return []
  return index.filter(i => (i.label + ' ' + i.where).toLowerCase().includes(q)).slice(0, limit)
}
